/**
 * Management dashboard (master-only) — full-page admin layout.
 *
 * Design ported from features/pulsarix-user-management.html: a fixed
 * full-viewport overlay above the app (sidebar + top bar + stat cards +
 * users table with presence filters + side column). Data is the real
 * `/api/admin/users` roster; fields the mockup invents (plan / funded /
 * approvals) are mapped to what we actually have (role = master|user, mode,
 * onboarding) or deferred. Backend gates with `require_master`.
 *
 * Polls every 15s while open so presence stays fresh.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector, useCan } from "@/state/hooks";
import { pushToast } from "@/state/slices/notificationsSlice";
import { api, ApiError } from "@/api/client";
import {
  ASSIGNABLE_ROLES,
  CAP,
  ROLE_META,
  roleLabel,
  rolesLabel,
} from "@/api/capabilities";
import { fmtAgo, fmtDate, fmtDateTime, initials } from "@/utils/format";
import type { AdminUser, AdminSummary, AdminRoleInfo } from "@/api/types";
import { TradersView } from "./TradersView";
import { SupportInbox, useInboxCounts } from "@/components/SupporterDashboard/SupportInbox";
import { UsageView } from "./UsageView";
import { useManagementViews, type View } from "./managementViews";
import styles from "./ManagementDashboard.module.css";

const ROLE_PILL: Record<string, string> = {
  owner: styles.roleOwner,
  admin: styles.roleAdmin,
  maintenance: styles.roleMaintenance,
  supporter: styles.roleSupporter,
  trader: styles.roleTrader,
  analyst: styles.roleAnalyst,
  viewer: styles.roleViewer,
};
const rolePill = (role: string) =>
  ROLE_PILL[ROLE_META[role]?.tone ?? "viewer"] ?? styles.roleViewer;

/** One pill per held role; a user with none is a Viewer. */
function RolePills({ roles }: { roles: readonly string[] }) {
  const shown = roles.length ? roles : ["viewer"];
  return (
    <span className={styles.pills}>
      {shown.map((r) => (
        <span key={r} className={`${styles.pill} ${rolePill(r)}`}>
          {roleLabel(r)}
        </span>
      ))}
    </span>
  );
}

/** Checkbox menu over the assignable roles. Admin already includes every
 *  other role, so while it is held the rest are shown but locked. Granting or
 *  removing admin needs `admins.manage` (the owner): without it the Admin box
 *  is locked, and so is the whole menu of someone who is an admin. */
function RolePicker({
  u,
  disabled,
  canManageAdmins,
  title,
  onToggle,
}: {
  u: AdminUser;
  disabled: boolean;
  canManageAdmins: boolean;
  title: string;
  onToggle: (u: AdminUser, role: string, on: boolean) => void;
}) {
  const isAdmin = u.roles.includes("admin");
  const ref = useRef<HTMLDetailsElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  // Open upward when the menu would not fit below — the last rows of the
  // table sit at the bottom of a scroll box, which would clip it.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!open || !menu || !ref.current) return;
    const anchor = ref.current.getBoundingClientRect();
    let bottom = window.innerHeight;
    let top = 0;
    for (let el = ref.current.parentElement; el; el = el.parentElement) {
      if (/(auto|scroll|hidden)/.test(getComputedStyle(el).overflowY)) {
        const box = el.getBoundingClientRect();
        bottom = Math.min(bottom, box.bottom);
        top = Math.max(top, box.top);
        break;
      }
    }
    const need = menu.offsetHeight + 8;
    const below = bottom - anchor.bottom;
    const above = anchor.top - top;
    setDropUp(below < need && above > below);
  }, [open]);
  // <details> only closes from its own summary — close it on a click
  // anywhere else, and on Escape.
  useEffect(() => {
    if (!open) return;
    const close = () => {
      if (ref.current) ref.current.open = false;
    };
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <details
      ref={ref}
      className={styles.rolePicker}
      onToggle={(e) => setOpen(e.currentTarget.open)}
    >
      <summary
        className={`${styles.roleSelect} ${disabled ? styles.roleSelectOff : ""}`}
        title={title}
        aria-disabled={disabled}
        onClick={(e) => {
          if (disabled) e.preventDefault();
        }}
      >
        Roles ▾
      </summary>
      <div
        ref={menuRef}
        className={`${styles.roleMenu} ${dropUp ? styles.roleMenuUp : ""}`}
        data-drop={dropUp ? "up" : "down"}
        role="group"
        aria-label={`Roles for ${u.email}`}
      >
        {ASSIGNABLE_ROLES.map((r) => (
          <label key={r} className={styles.roleOpt}>
            <input
              type="checkbox"
              checked={u.roles.includes(r)}
              disabled={
                disabled || (isAdmin && r !== "admin") || (r === "admin" && !canManageAdmins)
              }
              onChange={(e) => onToggle(u, r, e.target.checked)}
            />
            {roleLabel(r)}
          </label>
        ))}
      </div>
    </details>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  termConnected?: boolean;
  onTerminalViewChange?: (active: boolean) => void;
  onTerminalRefresh?: () => void;
}

const REFRESH_MS = 15000;
type Filter = "all" | "online" | "away" | "offline";

const GRADIENTS = [
  "#3D7BFF,#9B7CFF",
  "#23D5A4,#1FA8C9",
  "#F4A23C,#FF6B7A",
  "#9B7CFF,#FF6B9D",
  "#3D7BFF,#23D5A4",
  "#F4C44C,#F4A23C",
  "#FF6B7A,#9B7CFF",
  "#1FA8C9,#3D7BFF",
];

const presClass = (p: AdminUser["presence"]) =>
  p === "online"
    ? styles.presOnline
    : p === "away"
      ? styles.presAway
      : styles.presOffline;

const statusClass = (p: AdminUser["presence"]) =>
  p === "online"
    ? styles.statusOnline
    : p === "away"
      ? styles.statusAway
      : styles.statusOffline;

/** Avatar with a presence dot — Google picture if present, else a gradient
 *  initials tile (deterministic colour by index). */
function Avatar({ u, i }: { u: AdminUser; i: number }) {
  return (
    <div
      className={styles.av}
      style={{
        background: u.picture
          ? "transparent"
          : `linear-gradient(135deg,${GRADIENTS[i % GRADIENTS.length]})`,
      }}
    >
      {u.picture ? (
        <img
          className={styles.avImg}
          src={u.picture}
          alt=""
          referrerPolicy="no-referrer"
        />
      ) : (
        initials(u.name, u.email)
      )}
      <span className={`${styles.pres} ${presClass(u.presence)}`} />
    </div>
  );
}

export function ManagementDashboard({
  open,
  onClose,
  termConnected = false,
  onTerminalViewChange,
  onTerminalRefresh,
}: Props) {
  const dispatch = useAppDispatch();
  const me = useAppSelector((s) => s.me);
  const canManageRoles = useCan(CAP.rolesManage);
  const canManageAdmins = useCan(CAP.adminsManage);
  const canManageUsers = useCan(CAP.usersManage);
  const canTerminal = useCan(CAP.terminalUse);
  // Approving a trader grants the ability to place real orders, so it has
  // its own capability rather than riding on users.manage.
  const canManageTraders = useCan(CAP.tradersManage);
  const canViewUsage = useCan(CAP.usageView);
  // The roster, sessions and role matrix all come from dashboard.view routes.
  const canDashboard = useCan(CAP.dashboardView);
  const [usageTick, setUsageTick] = useState(0);
  // Each view is shown only with the capability its API requires (the shared
  // rule in managementViews.ts); an unavailable pick falls back to the first
  // allowed view in nav order.
  const { allowed, views } = useManagementViews();
  const [picked, setView] = useState<View | null>(null);
  const view: View | null = picked && allowed[picked] ? picked : (views[0] ?? null);
  // Inbox counts live here, not in SupportInbox, so the nav badge stays
  // current whichever view is open.
  const { counts: inboxCounts, refresh: refreshInboxCounts } = useInboxCounts(
    open && allowed.support,
  );
  const [supportRefresh, setSupportRefresh] = useState(0);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [query, setQuery] = useState("");
  const [busySub, setBusySub] = useState<string | null>(null);
  const [roleMatrix, setRoleMatrix] = useState<AdminRoleInfo[] | null>(null);
  const [matrixCaps, setMatrixCaps] = useState<string[]>([]);

  const load = useCallback(async (showSpinner: boolean) => {
    if (showSpinner) setStatus("loading");
    try {
      const res = await api.adminUsers();
      setUsers(res.users);
      setSummary(res.summary);
      setStatus("idle");
      setError(null);
    } catch (e) {
      setStatus("error");
      setError(e instanceof ApiError ? e.message : "Failed to load users");
    }
  }, []);

  // Run a row action, then toast + refresh the roster.
  const act = useCallback(
    async (sub: string, fn: () => Promise<unknown>, okTitle: string) => {
      setBusySub(sub);
      try {
        await fn();
        dispatch(pushToast({ kind: "success", title: okTitle }));
        await load(false);
      } catch (e) {
        dispatch(
          pushToast({
            kind: "error",
            title: "Action failed",
            message: e instanceof ApiError ? e.message : "Network error",
          }),
        );
      } finally {
        setBusySub(null);
      }
    },
    [dispatch, load],
  );

  const onToggleRole = (u: AdminUser, role: string, on: boolean) => {
    const next = on
      ? role === "admin"
        ? ["admin"]
        : [...u.roles, role]
      : u.roles.filter((r) => r !== role);
    const ask = on
      ? role === "admin"
        ? `Make ${u.email} an Admin? Admin includes every other role.`
        : `Give ${u.email} the ${roleLabel(role)} role?`
      : `Remove the ${roleLabel(role)} role from ${u.email}?`;
    if (!window.confirm(ask)) return;
    void act(
      u.sub,
      () => api.adminSetRoles(u.sub, next),
      `Roles set to ${rolesLabel(next)}`,
    );
  };

  const onRevoke = (u: AdminUser) => {
    if (!window.confirm(`Sign out all sessions for ${u.email}?`)) return;
    void act(u.sub, () => api.adminRevokeSessions(u.sub), "Sessions revoked");
  };

  // Lazy-load the role→capability matrix the first time the Roles view opens.
  useEffect(() => {
    if (!open || view !== "roles" || roleMatrix) return;
    void (async () => {
      try {
        const res = await api.adminRoles();
        setRoleMatrix(res.roles);
        setMatrixCaps(res.capabilities);
      } catch {
        /* surfaced via the empty state */
      }
    })();
  }, [open, view, roleMatrix]);

  useEffect(() => {
    if (!open || !canDashboard) return;
    void load(true);
    const id = window.setInterval(() => void load(false), REFRESH_MS);
    return () => window.clearInterval(id);
  }, [open, canDashboard, load]);

  // Closing while a terminal session is live: the confirm guard lives in
  // Header's handleAdminClose (which is the `onClose` prop here). Just
  // forward the call — the guard runs once at the top level.
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  // Phase 5: notify Header when the terminal view becomes active/inactive.
  // Including `open` in deps ensures terminalActive drops to false when the
  // dashboard closes — guaranteeing a false→true transition on reopen so
  // TerminalPanel's session-info effect always re-fires when returning to it.
  useEffect(() => {
    onTerminalViewChange?.(open && view === "terminal");
  }, [open, view, onTerminalViewChange]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return users.filter((u) => {
      if (filter !== "all" && u.presence !== filter) return false;
      if (!q) return true;
      return (
        u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
      );
    });
  }, [users, filter, query]);

  const onlineUsers = useMemo(
    () => users.filter((u) => u.presence === "online"),
    [users],
  );
  const pendingUsers = useMemo(
    () => users.filter((u) => !u.is_master && !u.profile_complete),
    [users],
  );

  // Capabilities dropped while open and nothing is left to show: close for
  // real, so Header's open state and the Escape listener are reset too.
  useEffect(() => {
    if (open && !view) onClose();
  }, [open, view, onClose]);

  if (!open || !view) return null;

  const s = summary;
  const FTABS: { key: Filter; label: string; n: number | undefined }[] = [
    { key: "all", label: "All", n: s?.total },
    { key: "online", label: "Online", n: s?.online },
    { key: "away", label: "Away", n: s?.away },
    { key: "offline", label: "Offline", n: s?.offline },
  ];

  return (
    <div className={styles.shell}>
      {/* ===== SIDEBAR ===== */}
      <aside className={styles.side}>
        <div className={styles.brand}>
          <img src="/logo-64.png" className={styles.mark} alt="" />
          <span className={styles.brandName}>
            Pulsa<span>rix</span>
          </span>
          <span className={styles.adm}>{rolesLabel(me.roles)}</span>
        </div>

        {canDashboard || canManageTraders ? (
          <div className={styles.navSec}>People</div>
        ) : null}
        {canDashboard ? (
          <>
            <button
              className={`${styles.nav} ${view === "users" ? styles.navOn : ""}`}
              onClick={() => setView("users")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="9" cy="8" r="3.2" />
                <path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5" />
                <path d="M17 8.5a3 3 0 010 5M19 20c0-2.4-1-3.8-2.5-4.6" />
              </svg>
              Users
              {s ? <span className={styles.navBadge}>{s.total}</span> : null}
            </button>
            <button
              className={`${styles.nav} ${view === "sessions" ? styles.navOn : ""}`}
              onClick={() => setView("sessions")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3 2" />
              </svg>
              Sessions
              {s && s.online > 0 ? <span className={styles.navBadge}>{s.online}</span> : null}
            </button>
            <button
              className={`${styles.nav} ${view === "roles" ? styles.navOn : ""}`}
              onClick={() => setView("roles")}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" />
              </svg>
              Roles &amp; Access
            </button>
          </>
        ) : null}
        {canManageTraders ? (
          <button
            className={`${styles.nav} ${view === "traders" ? styles.navOn : ""}`}
            onClick={() => setView("traders")}
            title="Trading access requests"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M3 17l5-5 4 3 6-7" />
              <path d="M14 8h5v5" />
            </svg>
            Traders
          </button>
        ) : null}

        {canViewUsage ? (
          <>
            <div className={styles.navSec}>Insights</div>
            <button
              className={`${styles.nav} ${view === "usage" ? styles.navOn : ""}`}
              onClick={() => setView("usage")}
              title="What users do in the app"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
              </svg>
              Usage
            </button>
          </>
        ) : null}

        {canTerminal || canDashboard ? (
          <div className={styles.navSec}>System</div>
        ) : null}
        {canTerminal ? (
          <button
            className={`${styles.nav} ${view === "terminal" ? styles.navOn : ""}`}
            onClick={() => setView("terminal")}
            title="Admin terminal (server shell)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="3" y="4" width="18" height="16" rx="2" />
              <path d="M7 9l3 3-3 3M13 15h4" />
            </svg>
            Terminal
            {termConnected ? <span className={styles.termDot} title="session live" /> : null}
          </button>
        ) : null}
        {/* Placeholder with no API yet; shown with the rest of the admin
            console (dashboard.view). */}
        {canDashboard ? (
          <button className={styles.nav} disabled title="Coming soon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M4 6h16M4 12h16M4 18h10" />
            </svg>
            Audit Log
            <span className={styles.navSoon}>soon</span>
          </button>
        ) : null}

        {allowed.support ? (
          <>
            <div className={styles.navSec}>Support</div>
            <button
              className={`${styles.nav} ${view === "support" ? styles.navOn : ""}`}
              onClick={() => setView("support")}
              title="Support tickets and the community room"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
              </svg>
              Support Inbox
              {inboxCounts && inboxCounts.unclaimed > 0 ? (
                <span className={styles.navBadge} title="Unclaimed tickets">
                  {inboxCounts.unclaimed}
                </span>
              ) : null}
            </button>
          </>
        ) : null}

        <div className={styles.grow} />
        <div className={styles.me}>
          <div className={styles.meAv}>
            {me.picture ? (
              <img src={me.picture} alt="" referrerPolicy="no-referrer" />
            ) : (
              initials(me.name, me.email)
            )}
          </div>
          <div>
            <div className={styles.meName}>{me.name || me.email || "—"}</div>
            <div className={styles.meRole}>{rolesLabel(me.roles)}</div>
          </div>
        </div>
      </aside>

      {/* ===== TOP BAR ===== */}
      <header className={styles.top}>
        <h1>
          {view === "usage"
            ? "Usage"
            : view === "roles"
            ? "Roles & Access"
            : view === "traders"
              ? "Trading Access"
              : view === "terminal"
                ? "Terminal"
                : view === "sessions"
                  ? "Sessions"
                  : view === "support"
                    ? "Support Inbox"
                    : "User Management"}
        </h1>
        <span className={styles.crumb}>
          {view === "usage"
            ? "/ Insights / Usage"
            : view === "roles"
            ? "/ People / Roles"
            : view === "traders"
              ? "/ People / Traders"
              : view === "terminal"
                ? "/ System / Terminal"
                : view === "sessions"
                  ? "/ People / Sessions"
                  : view === "support"
                    ? "/ Support / Inbox"
                    : "/ People / Users"}
        </span>
        {view === "users" ? (
          <label className={styles.search}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-4-4" />
            </svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email…"
            />
          </label>
        ) : (
          <span style={{ marginLeft: "auto" }} />
        )}
        <button
          className={styles.ibtn}
          onClick={() => {
            if (view === "roles") setRoleMatrix(null);
            else if (view === "usage") setUsageTick((t) => t + 1);
            else if (view === "terminal") onTerminalRefresh?.();
            else if (view === "support") {
              setSupportRefresh((k) => k + 1);
              refreshInboxCounts();
            }
            else void load(true);
          }}
          title="Refresh"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 12a9 9 0 11-2.6-6.4M21 4v5h-5" />
          </svg>
        </button>
        <button className={styles.ibtn} onClick={handleClose} title="Close (Esc)">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </header>

      {/* ===== MAIN ===== */}
      <main className={styles.main}>
        {view === "usage" ? (
          <UsageView refreshTick={usageTick} />
        ) : view === "roles" ? (
          <RolesMatrix roles={roleMatrix} caps={matrixCaps} />
        ) : view === "traders" ? (
          <TradersView />
        ) : view === "support" ? (
          <SupportInbox counts={inboxCounts} refreshKey={supportRefresh} />
        ) : view === "sessions" ? (
          <SessionsView
            users={users}
            status={status}
            canManageUsers={canManageUsers}
            busySub={busySub}
            me={me}
            onRevoke={onRevoke}
          />
        ) : view === "users" ? (
        <>
        {/* stat cards */}
        <div className={styles.stats}>
          <Stat
            icon="users"
            iconClass={styles.icBlue}
            label="Total Users"
            value={s?.total ?? "—"}
            delta={s ? `+${s.new_7d} this week` : undefined}
          />
          <Stat
            icon="online"
            iconClass={styles.icGreen}
            label="Online Now"
            value={s?.online ?? "—"}
            valueClass={styles.up}
            pulse
            sub={
              s && s.total
                ? `${Math.round((s.online / s.total) * 100)}% of users active`
                : undefined
            }
          />
          <Stat
            icon="new"
            iconClass={styles.icAmber}
            label="New This Week"
            value={s?.new_7d ?? "—"}
          />
          <Stat
            icon="pending"
            iconClass={styles.icViolet}
            label="Pending Onboarding"
            value={s?.pending_onboarding ?? "—"}
            sub="awaiting profile"
          />
          <Stat
            icon="master"
            iconClass={styles.icGold}
            label="Masters"
            value={s?.masters ?? "—"}
          />
        </div>

        <div className={styles.grid2}>
          {/* USERS TABLE */}
          <div className={styles.card}>
            <div className={styles.cardHead}>
              <h2>All Users</h2>
              <div className={styles.ftabs}>
                {FTABS.map((t) => (
                  <button
                    key={t.key}
                    className={`${styles.ftab} ${
                      filter === t.key ? styles.ftabOn : ""
                    }`}
                    onClick={() => setFilter(t.key)}
                  >
                    {t.label}
                    {t.n != null ? <span className={styles.n}>{t.n}</span> : null}
                  </button>
                ))}
              </div>
            </div>
            {status === "error" ? (
              <div className={styles.notice}>{error}</div>
            ) : status === "loading" && users.length === 0 ? (
              <div className={styles.notice}>Loading users…</div>
            ) : (
              <>
                <div className={styles.tableScroll}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Status</th>
                      <th>Role</th>
                      <th>Mode</th>
                      <th>Registered</th>
                      <th title="Most recent activity in the app (presence), not the sign-in date — a sign-in lasts 30 days, so it says nothing about whether they still use it.">
                        Last Seen
                      </th>
                      <th className={styles.r}>Sessions</th>
                      <th className={styles.r}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u, i) => (
                      <tr key={u.sub}>
                        <td>
                          <div className={styles.user}>
                            <Avatar u={u} i={i} />
                            <div>
                              <div className={styles.uname}>
                                {u.name || "—"}
                              </div>
                              <div className={styles.umail}>{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`${styles.status} ${statusClass(
                              u.presence,
                            )}`}
                          >
                            <span className={styles.sdot} />
                            {u.presence === "online"
                              ? "Online"
                              : u.presence === "away"
                                ? "Away"
                                : "Offline"}
                          </span>
                        </td>
                        <td>
                          <RolePills roles={u.roles} />
                        </td>
                        <td>
                          <span
                            className={`${styles.mode} ${
                              u.mode === "live" ? styles.modeLive : ""
                            }`}
                          >
                            {u.mode}
                          </span>
                        </td>
                        <td className={styles.lastseen}>
                          {fmtDate(u.created_at)}
                        </td>
                        <td
                          className={styles.lastseen}
                          title={
                            u.last_seen
                              ? `Last seen ${fmtDateTime(u.last_seen)} · signed in ${fmtDate(u.last_signin_at)}`
                              : `No activity recorded yet · signed in ${fmtDate(u.last_signin_at)}`
                          }
                        >
                          {fmtAgo(u.last_seen)}
                        </td>
                        <td className={`${styles.lastseen} ${styles.r}`}>
                          {u.active_sessions}
                        </td>
                        <td className={styles.r}>
                          {(() => {
                            const isSelf = u.email === me.email;
                            const busy = busySub === u.sub;
                            return (
                              <div className={styles.rowacts}>
                                {canManageRoles ? (
                                  <RolePicker
                                    u={u}
                                    disabled={
                                      isSelf ||
                                      u.role_locked ||
                                      busy ||
                                      (u.roles.includes("admin") && !canManageAdmins)
                                    }
                                    canManageAdmins={canManageAdmins}
                                    title={
                                      u.role_locked
                                        ? "Owner via server config — locked"
                                        : isSelf
                                          ? "You can't change your own roles"
                                          : u.roles.includes("admin") && !canManageAdmins
                                            ? "Only the owner can change an admin's roles"
                                            : "Change roles"
                                    }
                                    onToggle={onToggleRole}
                                  />
                                ) : null}
                                {canManageUsers ? (
                                  <button
                                    className={`${styles.ra} ${styles.danger}`}
                                    disabled={
                                      isSelf || busy || u.active_sessions === 0
                                    }
                                    title={
                                      isSelf
                                        ? "You can't revoke your own sessions"
                                        : u.active_sessions === 0
                                          ? "No active sessions"
                                          : "Revoke sessions (force sign-out)"
                                    }
                                    onClick={() => onRevoke(u)}
                                  >
                                    <svg
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                    >
                                      <path d="M16 17l5-5-5-5M21 12H9M12 19H5V5h7" />
                                    </svg>
                                  </button>
                                ) : null}
                                {!canManageRoles && !canManageUsers ? (
                                  <span className={styles.umail}>—</span>
                                ) : null}
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 ? (
                      <tr>
                        <td
                          colSpan={8}
                          style={{ textAlign: "center", padding: 30 }}
                          className={styles.umail}
                        >
                          No users match your filter.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
                </div>
                <div className={styles.footnote}>
                  Showing {filtered.length} of {users.length} users
                </div>
              </>
            )}
          </div>

          {/* SIDE COLUMN */}
          <div className={styles.sideCol}>
            <div className={`${styles.card} ${styles.sideCard}`}>
              <div className={styles.cardHead}>
                <h2>Pending Onboarding</h2>
                {pendingUsers.length > 0 ? (
                  <span className={styles.headPill}>
                    {pendingUsers.length} new
                  </span>
                ) : null}
              </div>
              <div className={styles.cardBody}>
              {pendingUsers.length === 0 ? (
                <div className={styles.emptyMini}>All caught up.</div>
              ) : (
                pendingUsers.slice(0, 6).map((u, i) => (
                  <div key={u.sub} className={styles.reg}>
                    <Avatar u={u} i={i + 2} />
                    <div className={styles.regMeta}>
                      <div className={styles.regName}>{u.name || "—"}</div>
                      <div className={styles.regEmail}>{u.email}</div>
                    </div>
                    <div className={styles.regWhen}>
                      {fmtAgo(u.created_at)}
                    </div>
                  </div>
                ))
              )}
              </div>
            </div>

            <div className={`${styles.card} ${styles.sideCard}`}>
              <div className={styles.cardHead}>
                <h2>Online Now</h2>
                <span className={styles.pulse} />
              </div>
              <div className={styles.cardBody}>
              {onlineUsers.length === 0 ? (
                <div className={styles.emptyMini}>Nobody online.</div>
              ) : (
                onlineUsers.slice(0, 8).map((u, i) => (
                  <div key={u.sub} className={styles.onlineItem}>
                    <Avatar u={u} i={i} />
                    <span className={styles.nm}>{u.name || u.email}</span>
                    <span className={styles.act}>{rolesLabel(u.roles)}</span>
                  </div>
                ))
              )}
              </div>
            </div>
          </div>
        </div>
        </>
        ) : null}

        {/* Phase 5: TerminalPanel is now mounted in Header (always alive) as
            a fixed overlay above this main area when the terminal tab is
            active. We keep a blank termLayer placeholder so the main area
            is clear (no users/roles content visible behind the overlay). */}
        {canTerminal && view === "terminal" ? (
          <div className={styles.termLayer} />
        ) : null}
      </main>
    </div>
  );
}

/* ---- active sessions view ---- */
function SessionsView({
  users,
  status,
  canManageUsers,
  busySub,
  me,
  onRevoke,
}: {
  users: AdminUser[];
  status: "idle" | "loading" | "error";
  canManageUsers: boolean;
  busySub: string | null;
  me: { email?: string };
  onRevoke: (u: AdminUser) => void;
}) {
  const active = users.filter((u) => u.active_sessions > 0);

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <h2>Active Sessions</h2>
        {active.length > 0 ? (
          <span className={styles.headPill}>{active.length} users</span>
        ) : null}
      </div>
      {status === "loading" && active.length === 0 ? (
        <div className={styles.notice}>Loading…</div>
      ) : active.length === 0 ? (
        <div className={styles.emptyMini}>No active sessions right now.</div>
      ) : (
        <div className={styles.tableScroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th className={styles.r}>Sessions</th>
                {canManageUsers ? <th className={styles.r}>Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {active.map((u, i) => {
                const isSelf = u.email === me.email;
                const busy = busySub === u.sub;
                return (
                  <tr key={u.sub}>
                    <td>
                      <div className={styles.user}>
                        <Avatar u={u} i={i} />
                        <div>
                          <div className={styles.uname}>{u.name || "—"}</div>
                          <div className={styles.umail}>{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <RolePills roles={u.roles} />
                    </td>
                    <td className={`${styles.lastseen} ${styles.r}`}>
                      {u.active_sessions}
                    </td>
                    {canManageUsers ? (
                      <td className={styles.r}>
                        <div className={styles.rowacts}>
                          <button
                            className={`${styles.ra} ${styles.danger}`}
                            disabled={isSelf || busy}
                            title={
                              isSelf
                                ? "You can't revoke your own sessions"
                                : "Revoke sessions (force sign-out)"
                            }
                            onClick={() => onRevoke(u)}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M16 17l5-5-5-5M21 12H9M12 19H5V5h7" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ---- roles × capabilities matrix (read-only) ---- */
function RolesMatrix({
  roles,
  caps,
}: {
  roles: AdminRoleInfo[] | null;
  caps: string[];
}) {
  if (!roles) return <div className={styles.notice}>Loading roles…</div>;
  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <h2>Role → Capability matrix</h2>
        <span className={styles.crumb} style={{ marginLeft: "auto" }}>
          fixed roles — defined in code
        </span>
      </div>
      <div className={styles.tableScroll}>
        <table className={styles.matrix}>
          <thead>
            <tr>
              <th>Capability</th>
              {roles.map((r) => (
                <th key={r.role} className={styles.matCol}>
                  {r.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {caps.map((cap) => (
              <tr key={cap}>
                <td className={styles.matCap}>{cap}</td>
                {roles.map((r) => (
                  <td key={r.role} className={styles.matCell}>
                    {r.capabilities.includes(cap) ? (
                      <span className={styles.matYes}>✓</span>
                    ) : (
                      <span className={styles.matNo}>·</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---- stat card ---- */
function Stat({
  icon,
  iconClass,
  label,
  value,
  valueClass,
  delta,
  sub,
  pulse,
}: {
  icon: "users" | "online" | "new" | "pending" | "master";
  iconClass: string;
  label: string;
  value: number | string;
  valueClass?: string;
  delta?: string;
  sub?: string;
  pulse?: boolean;
}) {
  return (
    <div className={styles.stat}>
      <div className={styles.statTop}>
        <span className={`${styles.ic} ${iconClass}`}>
          <StatIcon icon={icon} />
        </span>
        <span className={styles.statLbl}>{label}</span>
        {pulse ? <span className={styles.pulse} /> : null}
      </div>
      <div className={`${styles.statVal} ${valueClass ?? ""}`}>{value}</div>
      {delta ? (
        <div className={styles.statDelta}>{delta}</div>
      ) : sub ? (
        <div className={styles.statDelta}>
          <span className={styles.muted}>{sub}</span>
        </div>
      ) : null}
    </div>
  );
}

function StatIcon({ icon }: { icon: string }) {
  switch (icon) {
    case "online":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="3" fill="currentColor" />
        </svg>
      );
    case "new":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <rect x="3" y="4" width="18" height="17" rx="2" />
          <path d="M3 9h18M8 2v4M16 2v4M9 13l2 2 4-4" />
        </svg>
      );
    case "pending":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      );
    case "master":
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5M17 8.5a3 3 0 010 5" />
        </svg>
      );
  }
}
